import React, { useState, useEffect } from 'react';
import { Box, Stack, Button } from '@mui/material';
import EditorPanel from './EditorPanel';
import PreviewPanel from './PreviewPanel';
import { Entry } from '../App';
import { sanitizeMarkdown } from '../utils/createEntry';

interface EntryItemProps {
  entry: Entry;
  setEntries: React.Dispatch<React.SetStateAction<Entry[]>>;
  isEditingAny: boolean;
  activeEditingId?: string;
}

const EntryItem: React.FC<EntryItemProps> = ({
  entry,
  setEntries,
  isEditingAny,
  activeEditingId,
}) => {
  const [localQrText, setLocalQrText] = useState(entry.qrText);
  const [localMarkdownText, setLocalMarkdownText] = useState(entry.markdownText);
  const [sanitizedHtml, setSanitizedHtml] = useState(entry.sanitizedHtml);

  useEffect(() => {
    let isMounted = true;

    const updateSanitizedHtml = async () => {
      const sanitized = await sanitizeMarkdown(entry.markdownText);
      if (isMounted) {
        setSanitizedHtml(sanitized);
        setLocalQrText(entry.qrText);
        setLocalMarkdownText(entry.markdownText);
      }
    };

    if (entry.isEditing) {
      updateSanitizedHtml();
    }

    return () => {
      isMounted = false;
    };
  }, [entry.isEditing, entry.qrText, entry.markdownText]);

  useEffect(() => {
    if (!entry.isEditing || !entry.showPreviewWhileEditing) return;

    const timeout = setTimeout(() => {
      sanitizeMarkdown(localMarkdownText).then(setSanitizedHtml);
    }, 300);

    return () => clearTimeout(timeout);
  }, [localMarkdownText, entry.isEditing, entry.showPreviewWhileEditing]);

  const handleSaveEntry = async () => {
    const hasChanged =
      localQrText !== entry.qrText || localMarkdownText !== entry.markdownText;

    if (!hasChanged) {
      setEntries(prev =>
        prev.map(e =>
          e.id === entry.id ? { ...e, isEditing: false, isNew: false } : e
        )
      );
      return;
    }

    const sanitized = await sanitizeMarkdown(localMarkdownText);
    setEntries(prev =>
      prev.map(e =>
        e.id === entry.id
          ? {
              ...e,
              qrText: localQrText,
              markdownText: localMarkdownText,
              sanitizedHtml: sanitized,
              isEditing: false,
              isNew: false,
            }
          : e
      )
    );
  };

  const handleCancel = () => {
    if (entry.isNew) {
      // Remove unsaved entry
      setEntries(prev => prev.filter(e => e.id !== entry.id));
    } else {
      // Revert to original entry
      setEntries(prev =>
        prev.map(e =>
          e.id === entry.id ? { ...e, isEditing: false } : e
        )
      );
    }
  };

  const toggleEdit = () => {
    if (entry.isEditing) {
      handleSaveEntry();
    } else {
      setEntries(prev =>
        prev.map(e =>
          e.id === entry.id ? { ...e, isEditing: true } : e
        )
      );
    }
  };

  const togglePreviewWhileEditing = () => {
    setEntries(prev =>
      prev.map(e =>
        e.id === entry.id
          ? { ...e, showPreviewWhileEditing: !e.showPreviewWhileEditing }
          : e
      )
    );
  };

  const deleteEntry = () => {
    setEntries(prev => prev.filter(e => e.id !== entry.id));
  };

  return (
    <Box className="no-page-break">
      {entry.isEditing ? (
        <>
          {entry.showPreviewWhileEditing ? (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              gap={2}
            >
              <EditorPanel
                qrText={localQrText}
                markdownText={localMarkdownText}
                setQrText={setLocalQrText}
                setMarkdownText={setLocalMarkdownText}
              />
              <PreviewPanel
                qrText={localQrText}
                sanitizedHtml={sanitizedHtml}
              />
            </Box>
          ) : (
            <EditorPanel
              qrText={localQrText}
              markdownText={localMarkdownText}
              setQrText={setLocalQrText}
              setMarkdownText={setLocalMarkdownText}
            />
          )}
        </>
      ) : (
        <PreviewPanel
          qrText={entry.qrText}
          sanitizedHtml={entry.sanitizedHtml}
        />
      )}

      {(!isEditingAny || activeEditingId === entry.id) && (
        <Stack className="no-print" direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={toggleEdit}>
            {entry.isEditing ? 'Save' : 'Edit'}
          </Button>
          <Button variant="outlined" color="error" onClick={deleteEntry}>
            Delete
          </Button>
          {entry.isEditing && (
            <>
              <Button variant="outlined" onClick={togglePreviewWhileEditing}>
                {entry.showPreviewWhileEditing ? 'Hide Preview' : 'Show Preview'}
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default EntryItem;
